import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { FiShield, FiMail, FiLock, FiAlertTriangle, FiArrowLeft, FiKey } from 'react-icons/fi';
import './Auth.css';

/**
 * Admin Login Page
 * SECURITY: Dedicated login for app administrators
 * - Only allows app_admin role users
 * - Supports Two-Factor Authentication
 * - Separate from regular user login
 * - Redirects to /admin/dashboard on success
 */
const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, verify2faLogin, isAuthenticated, user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 2FA state
  const [show2fa, setShow2fa] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  // If already authenticated as admin, redirect to dashboard
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      if (user?.role === 'app_admin') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        // Logged in but not admin - show error
        setError('Access denied. Admin privileges required.');
      }
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    try {
      const result = await login(email.trim(), password);

      // Check if 2FA is required
      if (result.requires2fa) {
        setChallengeToken(result.challengeToken);
        setShow2fa(true);
        setLoading(false);
        return;
      }

      // Check if user is admin
      if (result?.user?.role !== 'app_admin') {
        setError('Access denied. This login is for administrators only.');
        // Clear the session since non-admin tried to login via admin portal
        localStorage.removeItem('token');
        return;
      }

      toast.success('Welcome, Admin!');
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      const message = err.response?.data?.message || 'Login failed. Please check your credentials.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setError('');

    if (!verifyCode) {
      setError('Please enter a verification code');
      return;
    }

    setLoading(true);
    try {
      const result = await verify2faLogin(challengeToken, verifyCode, useRecoveryCode);

      // Check if user is admin
      if (result?.user?.role !== 'app_admin') {
        setError('Access denied. This login is for administrators only.');
        localStorage.removeItem('token');
        resetToLogin();
        return;
      }

      if (result.warning) {
        toast.warning(result.warning);
      }

      toast.success('Welcome, Admin!');
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      if (err.response?.status === 401 && err.response?.data?.message?.includes('expired')) {
        setError('Session expired. Please log in again.');
        resetToLogin();
      } else {
        setError(err.response?.data?.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetToLogin = () => {
    setShow2fa(false);
    setChallengeToken('');
    setVerifyCode('');
    setUseRecoveryCode(false);
    setPassword('');
    setError('');
  };

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-loading">
            <div className="spinner"></div>
            <p>Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  // 2FA Verification Screen
  if (show2fa) {
    return (
      <div className="auth-container admin-login">
        <div className="auth-card">
          <div className="auth-header">
            <div className="tfa-icon">
              <FiShield size={48} color="#6C5CE7" />
            </div>
            <h1>Two-Factor Authentication</h1>
            <p className="auth-subtitle">
              {useRecoveryCode
                ? 'Enter a recovery code'
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          {error && (
            <div className="auth-error">
              <FiAlertTriangle style={{ marginRight: 8 }} />
              {error}
            </div>
          )}

          <form onSubmit={handleVerify2fa}>
            <div className="form-group">
              <label htmlFor="verifyCode">
                {useRecoveryCode ? 'Recovery Code' : 'Verification Code'}
              </label>
              <input
                type="text"
                id="verifyCode"
                value={verifyCode}
                onChange={(e) => setVerifyCode(
                  useRecoveryCode
                    ? e.target.value.toUpperCase()
                    : e.target.value.replace(/\D/g, '').slice(0, 6)
                )}
                required
                placeholder={useRecoveryCode ? 'XXXX-XXXX' : '000000'}
                autoFocus
                autoComplete="one-time-code"
                disabled={loading}
                style={useRecoveryCode ? {} : {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  letterSpacing: '0.3rem',
                }}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading}
              style={{ backgroundColor: '#6C5CE7' }}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>

          <div className="admin-login-footer">
            <button
              type="button"
              onClick={() => {
                setUseRecoveryCode(!useRecoveryCode);
                setVerifyCode('');
                setError('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#6C5CE7',
                cursor: 'pointer',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                width: '100%',
                marginTop: 16,
              }}
            >
              <FiKey size={14} />
              {useRecoveryCode ? 'Use authenticator code instead' : 'Use a recovery code instead'}
            </button>

            <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #ecf0f1' }} />

            <button
              type="button"
              onClick={resetToLogin}
              style={{
                background: 'none',
                border: 'none',
                color: '#7F8C8D',
                cursor: 'pointer',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                width: '100%',
              }}
            >
              <FiArrowLeft size={14} />
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container admin-login">
      <div className="auth-card">
        <div className="auth-header">
          <div className="admin-logo">
            <FiShield size={48} color="#6C5CE7" />
          </div>
          <h1>Admin Portal</h1>
          <p className="auth-subtitle">Hisab OK Hai - Administrator Access</p>
        </div>

        {error && (
          <div className="auth-error">
            <FiAlertTriangle style={{ marginRight: 8 }} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">
              <FiMail style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Admin Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@hisabokhai.com"
              required
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              <FiLock style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
            style={{ backgroundColor: '#6C5CE7' }}
          >
            {loading ? 'Signing in...' : 'Sign In as Admin'}
          </button>
        </form>

        <div className="admin-login-footer">
          <p style={{ fontSize: '0.85rem', color: '#7F8C8D', marginTop: 16 }}>
            <FiShield style={{ marginRight: 4, verticalAlign: 'middle' }} />
            This is a restricted area for authorized administrators only.
          </p>
          <a
            href="/admin/forgot-password"
            style={{ fontSize: '0.85rem', color: '#6C5CE7', marginTop: 8, display: 'block' }}
          >
            Forgot your password?
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
